/**
 * Billing Service
 *
 * Provides API functions for billing, subscription, and payment management
 * Integrates with Stripe for payment processing
 *
 * Standards: .equilateral-standards/serverless-saas-aws/api_standards.md
 * Pattern: Method-specific endpoints with proper parameter handling
 */

import { getApiBaseUrl, API_ENDPOINTS } from './api/endpoints';

// Type Definitions

export type BillingPeriod = 'monthly' | 'annual';

export type SubscriptionStatus =
  | 'active'
  | 'trialing'
  | 'past_due'
  | 'canceled'
  | 'incomplete'
  | 'incomplete_expired'
  | 'unpaid';

export type PricingTier = 'free' | 'starter' | 'professional' | 'enterprise';

export interface PricingPlan {
  tier: PricingTier;
  name: string;
  description: string;
  monthlyPrice: number;
  annualPrice: number;
  features: string[];
  agentLimit: number;
  executionLimit: number;
  storageLimit: string;
  supportLevel: string;
  isPopular?: boolean;
}

export interface Subscription {
  subscription_id: string;
  client_id: string;
  stripe_subscription_id: string;
  stripe_customer_id: string;
  tier: PricingTier;
  status: SubscriptionStatus;
  billing_period: BillingPeriod;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  canceled_at: string | null;
  trial_start: string | null;
  trial_end: string | null;
  created_at: string;
  updated_at: string;
}

export interface UsageStats {
  period_start: string;
  period_end: string;
  executions_used: number;
  executions_limit: number;
  agents_used: number;
  agents_limit: number;
  storage_used_gb: number;
  storage_limit_gb: number;
  api_calls: number;
  api_calls_limit: number;
}

export interface PaymentMethod {
  id: string;
  type: 'card' | 'bank_account';
  brand?: string;
  last4: string;
  exp_month?: number;
  exp_year?: number;
  is_default: boolean;
  created_at: string;
}

export interface Invoice {
  id: string;
  invoice_number: string;
  amount_due: number;
  amount_paid: number;
  currency: string;
  status: 'draft' | 'open' | 'paid' | 'void' | 'uncollectible';
  period_start: string;
  period_end: string;
  invoice_pdf: string | null;
  hosted_invoice_url: string | null;
  created_at: string;
  paid_at: string | null;
}

export interface CheckoutSession {
  session_id: string;
  checkout_url: string;
}

export interface CustomerPortalSession {
  url: string;
}

export interface APIResponse<T> {
  success: boolean;
  message?: string;
  data: {
    Records: T[];
  };
  meta?: {
    Total_Records: number;
    Request_ID: string;
    Timestamp: string;
  };
}

// API Client Helper
async function makeAuthorizedAPICall<T>(
  endpoint: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  body?: Record<string, unknown>,
  queryParams?: Record<string, string>
): Promise<APIResponse<T>> {
  const idToken = localStorage.getItem('accessToken');

  if (!idToken) {
    throw new Error('No ID token found. Please log in.');
  }

  const baseUrl = getApiBaseUrl();
  let url = `${baseUrl}${endpoint}`;

  if (queryParams && (method === 'GET' || method === 'DELETE')) {
    const params = new URLSearchParams(queryParams);
    url = `${baseUrl}${endpoint}?${params.toString()}`;
  }

  const options: RequestInit = {
    method,
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
  };

  if (body && (method === 'POST' || method === 'PUT')) {
    options.body = JSON.stringify(body);
  }

  const response = await fetch(url, options);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `API call failed: ${response.statusText}`);
  }

  return response.json();
}

// Billing Endpoints - all from centralized API_ENDPOINTS
const BILLING_ENDPOINTS = {
  SUBSCRIPTION: API_ENDPOINTS.BILLING.SUBSCRIPTION,
  USAGE: API_ENDPOINTS.BILLING.USAGE,
  PAYMENT_METHODS: API_ENDPOINTS.BILLING.PAYMENT_METHODS,
  INVOICES: API_ENDPOINTS.BILLING.INVOICES,
  CHECKOUT: API_ENDPOINTS.BILLING.CHECKOUT_SESSION,
  PORTAL: API_ENDPOINTS.BILLING.CUSTOMER_PORTAL,
  PLANS: API_ENDPOINTS.BILLING.PLANS,
  CANCEL: API_ENDPOINTS.BILLING.CANCEL,
};

/**
 * Get current subscription details
 *
 * GET /api/billing/subscription
 */
export async function getSubscription(): Promise<APIResponse<Subscription>> {
  return makeAuthorizedAPICall<Subscription>(
    BILLING_ENDPOINTS.SUBSCRIPTION,
    'GET'
  );
}

/**
 * Get current period usage stats
 *
 * GET /api/billing/usage
 */
export async function getUsageStats(): Promise<APIResponse<UsageStats>> {
  return makeAuthorizedAPICall<UsageStats>(
    BILLING_ENDPOINTS.USAGE,
    'GET'
  );
}

/**
 * Get available pricing plans
 *
 * GET /api/billing/plans
 */
export async function getPricingPlans(): Promise<APIResponse<PricingPlan>> {
  return makeAuthorizedAPICall<PricingPlan>(
    BILLING_ENDPOINTS.PLANS,
    'GET'
  );
}

/**
 * Get saved payment methods
 *
 * GET /api/billing/payment-methods
 */
export async function getPaymentMethods(): Promise<APIResponse<PaymentMethod>> {
  return makeAuthorizedAPICall<PaymentMethod>(
    BILLING_ENDPOINTS.PAYMENT_METHODS,
    'GET'
  );
}

/**
 * Set default payment method
 *
 * PUT /api/billing/payment-methods
 */
export async function setDefaultPaymentMethod(paymentMethodId: string): Promise<APIResponse<PaymentMethod>> {
  return makeAuthorizedAPICall<PaymentMethod>(
    BILLING_ENDPOINTS.PAYMENT_METHODS,
    'PUT',
    { payment_method_id: paymentMethodId, set_default: true }
  );
}

/**
 * Delete a payment method
 *
 * DELETE /api/billing/payment-methods?id=xxx
 */
export async function deletePaymentMethod(paymentMethodId: string): Promise<APIResponse<{ deleted: boolean }>> {
  return makeAuthorizedAPICall<{ deleted: boolean }>(
    BILLING_ENDPOINTS.PAYMENT_METHODS,
    'DELETE',
    undefined,
    { id: paymentMethodId }
  );
}

/**
 * Get invoice history
 *
 * GET /api/billing/invoices?limit=xxx
 */
export async function getInvoices(limit: number = 10): Promise<APIResponse<Invoice>> {
  return makeAuthorizedAPICall<Invoice>(
    BILLING_ENDPOINTS.INVOICES,
    'GET',
    undefined,
    { limit: limit.toString() }
  );
}

/**
 * Create a checkout session for subscription upgrade/change
 *
 * POST /api/billing/checkout
 */
export async function createCheckoutSession(
  tier: PricingTier,
  billingPeriod: BillingPeriod,
  successUrl: string,
  cancelUrl: string
): Promise<APIResponse<CheckoutSession>> {
  return makeAuthorizedAPICall<CheckoutSession>(
    BILLING_ENDPOINTS.CHECKOUT,
    'POST',
    {
      tier,
      billing_period: billingPeriod,
      success_url: successUrl,
      cancel_url: cancelUrl,
    }
  );
}

/**
 * Create a customer portal session for managing billing
 *
 * POST /api/billing/portal
 */
export async function createPortalSession(returnUrl: string): Promise<APIResponse<CustomerPortalSession>> {
  return makeAuthorizedAPICall<CustomerPortalSession>(
    BILLING_ENDPOINTS.PORTAL,
    'POST',
    { return_url: returnUrl }
  );
}

/**
 * Cancel subscription
 *
 * POST /api/billing/cancel
 */
export async function cancelSubscription(
  cancelImmediately: boolean = false
): Promise<APIResponse<Subscription>> {
  return makeAuthorizedAPICall<Subscription>(
    BILLING_ENDPOINTS.CANCEL,
    'POST',
    { cancel_immediately: cancelImmediately }
  );
}

/**
 * Resume a canceled subscription (if within grace period)
 *
 * POST /api/billing/subscription
 */
export async function resumeSubscription(): Promise<APIResponse<Subscription>> {
  return makeAuthorizedAPICall<Subscription>(
    BILLING_ENDPOINTS.SUBSCRIPTION,
    'POST',
    { action: 'resume' }
  );
}

// Utility Functions

/**
 * Format currency amount for display
 */
export function formatCurrency(amount: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount / 100); // Stripe amounts are in cents
}

/**
 * Format date for display
 */
export function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Get subscription status display info
 */
export function getStatusInfo(status: SubscriptionStatus): { label: string; color: string } {
  const statusMap: Record<SubscriptionStatus, { label: string; color: string }> = {
    active: { label: 'Active', color: 'success' },
    trialing: { label: 'Trial', color: 'info' },
    past_due: { label: 'Past Due', color: 'warning' },
    canceled: { label: 'Canceled', color: 'failure' },
    incomplete: { label: 'Incomplete', color: 'warning' },
    incomplete_expired: { label: 'Expired', color: 'failure' },
    unpaid: { label: 'Unpaid', color: 'failure' },
  };

  return statusMap[status] || { label: status, color: 'gray' };
}

/**
 * Get usage percentage and status color
 */
export function getUsagePercentage(used: number, limit: number): { percentage: number; color: string } {
  if (limit === 0) return { percentage: 0, color: 'gray' };

  const percentage = Math.round((used / limit) * 100);

  let color = 'success';
  if (percentage >= 90) {
    color = 'failure';
  } else if (percentage >= 75) {
    color = 'warning';
  }

  return { percentage, color };
}

/**
 * Check if response is successful
 */
export function isSuccessResponse<T>(response: APIResponse<T>): boolean {
  return response.success === true;
}

// Note: No fallback pricing plans - getPricingPlans() must succeed or fail loud
